import { BOARD_SIZE, type Coord } from "@empire/core-sim";

/**
 * Isometric projection for the board (04 §2).
 *
 * A 2:1 projection — a tile is twice as wide as it is tall — which is the
 * standard for the genre and the reason the grid reads as a diamond rather than
 * a rotated square. Sizes are in CSS pixels at scale 1; the view scales the
 * whole container to fit a phone.
 */

export const TILE_W = 64;
/**
 * The narrowest a tile may be drawn, in CSS px, where people build with a
 * finger (editor, tutorial build steps on phones). 72×36 — the friends test
 * (Sep 2026) missed tiles at ~51 px.
 */
export const FINGER_TILE_PX = 72;
export const TILE_H = 32;

export interface Point {
  x: number;
  y: number;
}

/** Grid coordinate -> screen point (the centre of the tile's diamond). */
export function gridToScreen(gx: number, gy: number): Point {
  return {
    x: (gx - gy) * (TILE_W / 2),
    y: (gx + gy) * (TILE_H / 2),
  };
}

/**
 * Screen point -> grid coordinate.
 *
 * The inverse of gridToScreen. Solving the projection gives CONTINUOUS
 * coordinates whose whole numbers land on tile CENTRES, not corners — so the
 * containing tile is the nearest whole number, and rounding is what finds it.
 *
 * Flooring instead looks plausible and is wrong by half a tile: a tap on the
 * visual middle of a tile would select its neighbour, and drag-painting would
 * lay road one tile off from the finger. Caught by the round-trip test rather
 * than by reasoning, which is why that test covers off-centre points too.
 */
export function screenToGrid(px: number, py: number): Coord {
  const gx = px / TILE_W + py / TILE_H;
  const gy = py / TILE_H - px / TILE_W;
  return { x: Math.round(gx), y: Math.round(gy) };
}

/** The four corners of a tile's diamond, clockwise from the top. */
export function diamond(gx: number, gy: number): number[] {
  const c = gridToScreen(gx, gy);
  return [
    c.x, c.y - TILE_H / 2,
    c.x + TILE_W / 2, c.y,
    c.x, c.y + TILE_H / 2,
    c.x - TILE_W / 2, c.y,
  ];
}

/**
 * Draw order for the 2.5D illusion: tiles further "back" must be drawn first,
 * or a tall block would be overlapped by the ground behind it.
 *
 * On an isometric grid that is simply x + y, and using it as the sprite's
 * zIndex is cheaper and steadier than sorting by screen y every frame.
 */
export function depth(gx: number, gy: number): number {
  return gx + gy;
}

/** Total pixel size of a board of this many tiles per side. */
export function boardSize(tiles: number): { width: number; height: number } {
  return { width: tiles * TILE_W, height: tiles * TILE_H };
}

/**
 * A tower's range, drawn on the isometric board.
 *
 * The simulation measures range as a straight-line distance in GRID space
 * (`distSq` in core-sim/fixed.ts), so the true shape is a circle on the grid.
 * Drawing that circle on screen means pushing it through the same projection
 * the tiles go through, and the projection is linear, so a circle comes out as
 * an axis-aligned ellipse.
 *
 * Working it through: a point at angle θ and radius R on the grid is
 * (R·cosθ, R·sinθ). Projected, that is
 *
 *   sx = (R·cosθ − R·sinθ)·TILE_W/2 = R·TILE_W/√2 · cos(θ + 45°)
 *   sy = (R·cosθ + R·sinθ)·TILE_H/2 = R·TILE_H/√2 · sin(θ + 45°)
 *
 * — an ellipse with these two semi-axes. The 45° phase only decides where the
 * drawing starts, not its shape, so it drops out.
 *
 * Drawing a plain circle of R·TILE_W/2 instead would be the obvious thing and
 * would be a lie: it would claim the tower covers tiles it cannot reach along
 * the vertical axis, which is precisely the judgement the ring exists to help
 * a defender make.
 */
export function rangeEllipse(rangeTiles: number): { rx: number; ry: number } {
  return {
    rx: (rangeTiles * TILE_W) / Math.SQRT2,
    ry: (rangeTiles * TILE_H) / Math.SQRT2,
  };
}

// ---------------------------------------------------------------------------
// Projections
// ---------------------------------------------------------------------------

/**
 * How a board is laid on the screen.
 *
 * Isometric is the look of the game — battles and replays. Building a defence
 * with a finger is another matter (playtest, 29 Sep 2026: "it is harder to
 * build the defence and place road or towers on the phone, the view should be
 * from above"). On an isometric board a tile is a 2:1 diamond — half as tall
 * as it is wide — and a road drawn "straight" has to be dragged diagonally.
 * Seen from directly above, every tile is a square as tall as it is wide, and
 * rows and columns run along the screen, so the finger goes where the eye
 * expects.
 *
 * Both projections keep the same origin convention so the view code can stay
 * one piece: the centre of tile (0,0) sits at y = 0, the board spans
 * x = ±width/2 around the world origin, and rows run downward.
 */
export interface Projection {
  readonly topDown: boolean;
  /** One tile's drawn size at scale 1, in CSS px. */
  readonly tileW: number;
  readonly tileH: number;
  /** The narrowest a tile may be drawn where people build with a finger. */
  readonly fingerTilePx: number;
  gridToScreen(gx: number, gy: number): Point;
  screenToGrid(px: number, py: number): Coord;
  /** The tile's outline as a flat polygon (a diamond, or a square). */
  outline(gx: number, gy: number): number[];
  depth(gx: number, gy: number): number;
  boardSize(tiles: number): { width: number; height: number };
  rangeEllipse(rangeTiles: number): { rx: number; ry: number };
}

export const ISO: Projection = {
  topDown: false,
  tileW: TILE_W,
  tileH: TILE_H,
  fingerTilePx: FINGER_TILE_PX,
  gridToScreen,
  screenToGrid,
  outline: diamond,
  depth,
  boardSize,
  rangeEllipse,
};

/** A top-down tile: square, the width of an isometric one. */
export const TOP_TILE = 64;
/**
 * A square 44 px across (the usual minimum touch target) is easier to hit
 * than the 72×36 diamond the friends test settled on — a diamond fills only
 * half of its box — and small enough that the whole road from the gate to
 * the castle fits a portrait phone without panning.
 */
export const TOP_FINGER_TILE_PX = 44;

/** Columns are centred on the world origin, so a 12-wide board spans ±6 tiles. */
function topOffset(): number {
  return (BOARD_SIZE - 1) / 2;
}

export const TOP: Projection = {
  topDown: true,
  tileW: TOP_TILE,
  tileH: TOP_TILE,
  fingerTilePx: TOP_FINGER_TILE_PX,
  gridToScreen: (gx, gy) => ({ x: (gx - topOffset()) * TOP_TILE, y: gy * TOP_TILE }),
  // Rounding, as in the isometric inverse: whole numbers are tile CENTRES.
  screenToGrid: (px, py) => ({
    x: Math.round(px / TOP_TILE + topOffset()),
    y: Math.round(py / TOP_TILE),
  }),
  outline: (gx, gy) => {
    const x = (gx - topOffset()) * TOP_TILE;
    const y = gy * TOP_TILE;
    const h = TOP_TILE / 2;
    return [x - h, y - h, x + h, y - h, x + h, y + h, x - h, y + h];
  },
  // Things further down the screen are nearer the viewer; x breaks ties.
  depth: (gx, gy) => gy + gx * 0.01,
  boardSize: (tiles) => ({ width: tiles * TOP_TILE, height: tiles * TOP_TILE }),
  // From straight above, range IS a circle.
  rangeEllipse: (rangeTiles) => ({ rx: rangeTiles * TOP_TILE, ry: rangeTiles * TOP_TILE }),
};
